import { useEffect } from 'react';
import { Dialog } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useForm } from '@mantine/form';

import { Button, IconButton, Input, Textarea } from 'ui';

const getDefaultValues = () => ({
  topic: '',
  details: '',
});

const SuggestTopicModal = ({ open, onClose, onSubmit }) => {
  const form = useForm({
    initialValues: getDefaultValues(),
    validate: {
      topic: (value) => (value.trim() ? null : 'Topic is required.'),
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (open) {
      const defaults = getDefaultValues();
      form.setValues(defaults);
      form.resetDirty(defaults);
      form.setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    const defaults = getDefaultValues();
    form.setValues(defaults);
    form.resetDirty(defaults);
    form.setErrors({});
    onClose();
  };

  const handleSubmit = () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    onSubmit({
      topic: form.values.topic.trim(),
      details: form.values.details.trim(),
    });

    handleClose();
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
      closeAfterTransition={false}
    >
      <div className="space-y-6 p-6">
        <div className="relative flex items-start">
          <div className="space-y-1 pr-10">
            <h3 className="text-h6 font-semibold">Add Suggestion</h3>
            <p className="text-base text-gray-600">
              Suggest a specific email topic or campaign idea you would like us to consider.
            </p>
          </div>

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
        </div>

        <div className="space-y-6">
          <Input
            label="Topic"
            value={form.values.topic}
            onChange={(e) => form.setFieldValue('topic', e.target.value)}
            size="xl"
            containerClassName="w-full"
            required
            helperText={form.errors.topic || 'Brief description of the email topic or campaign idea'}
            error={!!form.errors.topic}
          />

          <Textarea
            label="Additional Details"
            value={form.values.details}
            onChange={(e) => form.setFieldValue('details', e.target.value)}
            size="lg"
            containerClassName="w-full"
            helperText="Any additional context or details (optional)"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button title="Cancel" onClick={handleClose} />
          <Button onClick={handleSubmit} color="primary" title="Add Suggestion" />
        </div>
      </div>
    </Dialog>
  );
};

SuggestTopicModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default SuggestTopicModal;
